import { isSuccess, isUnauthorized } from '@yimoka/shared';
import { action, define, observable } from '@yimoka/store';

import { authAccountEntity, type AuthAccountEntity } from '@/entities/auth-account';
import {
  clearAuthSession,
  getUserToken,
  setUser,
  setUserToken,
} from '@/session/auth-session';
import { normalizeInternalRedirect } from '@/utils/auth-redirect';

type Navigate = (to: string, options?: { replace?: boolean }) => unknown;
type TenantJoinAddStatus = 'checking' | 'error' | 'ready';

/** TenantJoinAddBiz 复用平台用户校验契约，保护创建学校申请页且不触发租户登录。 */
export class TenantJoinAddBiz {
  status: TenantJoinAddStatus = 'checking';
  currentPath = '/tenant/join/add';
  private validationVersion = 0;

  /** 初始化创建学校页的响应式状态，并允许测试注入隔离的账号 Entity。 */
  constructor(private readonly accountEntity: AuthAccountEntity = authAccountEntity) {
    define(this, {
      status: observable,
      currentPath: observable,
      mount: action,
    });
  }

  /** userInfoStore 主动确认平台用户 token，行为与旧 NeedUser 守卫保持一致。 */
  get userInfoStore() {
    return this.accountEntity.userInfoStore;
  }

  /** mount 校验平台用户；未登录或 token 失效时完整保留当前申请页地址返回登录。 */
  mount = async (navigate: Navigate, currentPath: string) => {
    this.validationVersion += 1;
    const { validationVersion } = this;
    this.status = 'checking';
    this.currentPath = normalizeInternalRedirect(currentPath) ?? '/tenant/join/add';

    if (!getUserToken()) {
      navigate(`/login?redirect=${encodeURIComponent(this.currentPath)}`, { replace: true });
      return;
    }

    const userRes = await this.userInfoStore.fetch();
    if (validationVersion !== this.validationVersion) return;
    if (isUnauthorized(userRes)) {
      clearAuthSession();
      navigate(`/login?redirect=${encodeURIComponent(this.currentPath)}`, { replace: true });
      return;
    }
    if (!isSuccess(userRes)) {
      this.status = 'error';
      return;
    }

    if (userRes.data?.token) setUserToken(userRes.data.token);
    if (userRes.data?.user) setUser(userRes.data.user);
    this.status = 'ready';
  };
}

export const tenantJoinAddBiz = new TenantJoinAddBiz();
